/**
 * One process-wide owner per reservation key.
 *
 * Multiple components can hold independent useChatSubmit hook instances. A
 * React mutation's `isPending` flag updates on the next render, which is too
 * late to stop two same-tick sends from both observing `activeSessionId=null`.
 * This synchronous reservation closes that gap. New-chat callers share the
 * default key; stale-session recovery uses the stale session id as its key so
 * unrelated chats can never collapse into the same replacement session.
 * Joiners wait for the owner's session id and enqueue their turn there; only
 * the owner may POST the first turn.
 */
interface FreshSessionReservation {
  token: symbol;
  session: Promise<string | null>;
}

const reservations = new Map<string, FreshSessionReservation>();

export interface FreshSessionLease {
  owner: boolean;
  session: Promise<string | null>;
  release: () => void;
}

export const reserveFreshSession = (
  create: () => Promise<string | null>,
  key = 'new-session'
): FreshSessionLease => {
  const currentReservation = reservations.get(key);
  if (currentReservation) {
    return {
      owner: false,
      session: currentReservation.session,
      release: () => undefined,
    };
  }

  const token = Symbol('agentforge-fresh-session');
  const session = Promise.resolve().then(create);
  reservations.set(key, { token, session });

  return {
    owner: true,
    session,
    release: () => {
      if (reservations.get(key)?.token === token) reservations.delete(key);
    },
  };
};
